import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { useSeo } from "../hooks/useSeo";

const IMG = "https://images.pexels.com/photos/13219418/pexels-photo-13219418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Business() {
  const { t, lang } = useI18n();
  useSeo({ title: t.seo.business.title, description: t.seo.business.description, path: "/bedrijfspanden", lang, image: IMG });

  return (
    <>
      <section className="container-wide pt-12 md:pt-20 pb-12 md:pb-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-12 flex items-center justify-between gap-6">
            <p className="eyebrow">{t.business.eyebrow}</p>
            <p className="hidden md:block text-xs uppercase tracking-[0.22em] text-muted-foreground font-bold">{t.editorial.businessPracticeLabel}</p>
          </div>
          <h1 className="lg:col-span-12 text-[40px] leading-[0.98] tracking-[-0.025em] font-medium text-foreground sm:text-[60px] lg:text-[96px] xl:text-[112px]">
            {t.business.title}
          </h1>
          <div className="lg:col-span-7">
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">{t.business.lead}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/contact?intent=intake" className="btn-primary" data-testid="business-cta">
                {t.business.cta} <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/tarieven" className="btn-ghost">{t.home.seePricing}</Link>
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="border-t border-border pt-6 space-y-4 max-w-md ml-auto text-sm">
              <div className="flex items-baseline justify-between gap-6"><span className="font-display text-2xl text-foreground">4–10</span><span className="text-muted-foreground text-xs uppercase tracking-[0.18em] text-right max-w-[180px]">{t.editorial.businessStat1Label}</span></div>
              <div className="flex items-baseline justify-between gap-6 border-t border-border pt-4"><span className="font-display text-2xl text-foreground">€500</span><span className="text-muted-foreground text-xs uppercase tracking-[0.18em] text-right max-w-[180px]">{t.editorial.pricingBusinessRow1}</span></div>
              <div className="flex items-baseline justify-between gap-6 border-t border-border pt-4"><span className="font-display text-2xl text-foreground">0,5–1%</span><span className="text-muted-foreground text-xs uppercase tracking-[0.18em] text-right max-w-[180px]">{t.editorial.businessStat3Label}</span></div>
            </div>
          </aside>
        </div>
      </section>

      <section className="container-wide pb-16 md:pb-24">
        <div className="relative overflow-hidden border-y border-border">
          <img src={IMG} alt="Modern office building — business property" className="w-full h-[42vh] md:h-[56vh] object-cover" />
          <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.25em] font-bold text-white/90 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-sm">
            Fig. 03 — Office tower, Brussels CBD
          </span>
        </div>
      </section>

      <section className="border-t border-border" data-testid="business-points">
        <div className="container-wide py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="eyebrow">{t.editorial.scopeEyebrow}</p>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] font-medium text-foreground leading-[1.05]">
              {t.editorial.businessScopeTitleBefore}<span className="italic text-primary">{t.editorial.businessScopeHighlight}</span>{t.editorial.businessScopeTitleAfter}
            </h2>
          </div>
          <ol className="lg:col-span-8 border-y border-border">
            {t.business.points.map((p, i) => (
              <li key={i} className="grid md:grid-cols-12 gap-6 py-7 md:py-8 border-b border-border last:border-b-0 items-baseline">
                <span className="md:col-span-1 font-display text-3xl text-accent leading-none">{String(i + 1).padStart(2, "0")}</span>
                <p className="md:col-span-11 text-lg md:text-xl text-foreground leading-snug">{p}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-t border-border bg-secondary/40">
        <div className="container-wide py-16 md:py-20 grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-8">
            <p className="font-display text-2xl md:text-3xl text-foreground tracking-[-0.015em] leading-snug">
              {t.editorial.businessCtaText} <span className="italic text-primary">{t.editorial.businessCtaHighlight}</span>
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/contact?intent=intake" className="btn-primary">{t.business.cta} <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
